import { Campo, useAuth } from '@eg/compartido'
import { SpringCheck, Step, StatusMark, Stepper, useAvisos } from '@eg/efectos'
import { ArrowLeft, ArrowRight, FileUp } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { z } from 'zod'
import { es } from '../../content/es'
import {
  documentosCompletos,
  ErrorRegistro,
  pasoInicial,
  PESO_MAXIMO,
  TIPOS_PERMITIDOS,
  useAccionesRegistro,
  useMiEmpresa,
  type MiEmpresa,
  type TipoDocumento,
} from './datos'
import { erroresPorCampo, esquemaFiscal, esquemaSanitario, esquemaSucursal } from './esquemas'

const t = es.registro

type Errores = Record<string, string>

/** Valida con zod y devuelve los datos limpios o los errores por campo. */
function validar<S extends z.ZodTypeAny>(esquema: S, valores: unknown): { datos: z.infer<S> } | { errores: Errores } {
  const r = esquema.safeParse(valores)
  return r.success ? { datos: r.data } : { errores: erroresPorCampo(r.error) }
}

function Botonera({ atras, enviando, etiqueta, deshabilitado }: { atras?: () => void; enviando: boolean; etiqueta?: string; deshabilitado?: boolean }) {
  return (
    <div className="botonera">
      {atras && (
        <button type="button" className="boton boton--secundario" onClick={atras} disabled={enviando}>
          <ArrowLeft className="icono" strokeWidth={1.75} aria-hidden="true" />
          {t.atras}
        </button>
      )}
      <button type="submit" className="boton boton--primario" disabled={enviando || deshabilitado} aria-busy={enviando}>
        {enviando ? t.guardando : (etiqueta ?? t.guardarYSeguir)}
        {!enviando && <ArrowRight className="icono" strokeWidth={1.75} aria-hidden="true" />}
      </button>
    </div>
  )
}

/** Paso 1: datos fiscales. Crea la empresa con la función de la base; si ya existe, solo actualiza contacto. */
function PasoFiscal({ datos, siguiente }: { datos: MiEmpresa | null | undefined; siguiente: () => void }) {
  const f = t.fiscal
  const { registrar, actualizarFiscal } = useAccionesRegistro()
  const avisar = useAvisos()
  const e = datos?.empresa
  const [v, setV] = useState({
    razon_social: e?.razon_social ?? '',
    rfc: e?.rfc ?? '',
    tipo: e?.tipo ?? '',
    codigo_postal_fiscal: e?.codigo_postal_fiscal ?? '',
    email_facturacion: e?.email_facturacion ?? '',
    telefono: e?.telefono ?? '',
  })
  const [errores, setErrores] = useState<Errores>({})
  const [privacidad, setPrivacidad] = useState(Boolean(e))
  const [errorPrivacidad, setErrorPrivacidad] = useState(false)
  const enviando = registrar.isPending || actualizarFiscal.isPending
  const campo = (k: keyof typeof v) => ({ value: v[k], onChange: (ev: { target: { value: string } }) => setV({ ...v, [k]: ev.target.value }) })

  async function enviar(ev: FormEvent) {
    ev.preventDefault()
    const r = validar(esquemaFiscal, v)
    if ('errores' in r) return setErrores(r.errores)
    setErrores({})
    if (!privacidad) return setErrorPrivacidad(true)
    try {
      if (e) await actualizarFiscal.mutateAsync({ id: e.id, d: r.datos })
      else await registrar.mutateAsync(r.datos)
      siguiente()
    } catch (err) {
      const codigo = err instanceof ErrorRegistro ? err.codigo : 'otro'
      if (codigo === 'rfc_repetido') setErrores({ rfc: f.rfcRepetido })
      else avisar({ tipo: 'error', titulo: es.errorTitulo, texto: codigo === 'maximo' ? f.maximo : t.errorGenerico })
    }
  }

  return (
    <form className="formulario" onSubmit={(ev) => void enviar(ev)} noValidate>
      <div className="formulario__rejilla">
        <div className="formulario__ancho">
          <Campo etiqueta={f.razonSocial} error={errores.razon_social}>
            {(c) => <input {...c} type="text" autoComplete="organization" required disabled={Boolean(e)} {...campo('razon_social')} />}
          </Campo>
        </div>
        <Campo etiqueta={f.rfc} ayuda={f.ayudaRfc} error={errores.rfc}>
          {(c) => <input {...c} type="text" autoCapitalize="characters" spellCheck={false} maxLength={13} required disabled={Boolean(e)} {...campo('rfc')} />}
        </Campo>
        <Campo etiqueta={f.tipo} error={errores.tipo}>
          {(c) => (
            <select {...c} required disabled={Boolean(e)} {...campo('tipo')}>
              <option value="">{t.eligeOpcion}</option>
              {Object.entries(f.tipos).map(([valor, texto]) => (
                <option key={valor} value={valor}>
                  {texto}
                </option>
              ))}
            </select>
          )}
        </Campo>
        <Campo etiqueta={f.codigoPostal} error={errores.codigo_postal_fiscal}>
          {(c) => <input {...c} type="text" inputMode="numeric" autoComplete="postal-code" maxLength={5} required {...campo('codigo_postal_fiscal')} />}
        </Campo>
        <Campo etiqueta={f.correo} error={errores.email_facturacion}>
          {(c) => <input {...c} type="email" autoComplete="email" inputMode="email" required {...campo('email_facturacion')} />}
        </Campo>
        <Campo etiqueta={f.telefono} error={errores.telefono}>
          {(c) => <input {...c} type="tel" autoComplete="tel" inputMode="tel" {...campo('telefono')} />}
        </Campo>
      </div>

      {!e && (
        <div className="privacidad">
          <SpringCheck
            label={t.privacidad}
            checked={privacidad}
            onChange={(x) => {
              setPrivacidad(x)
              if (x) setErrorPrivacidad(false)
            }}
            strike="none"
            doneOpacity={1}
            color="var(--text)"
            fillColor="var(--accent)"
            checkColor="var(--accent-ink)"
            ariaLabel={t.privacidad}
          />
          {errorPrivacidad && (
            <p className="campo__error" role="alert">
              {t.privacidadError}
            </p>
          )}
        </div>
      )}
      <Botonera enviando={enviando} />
    </form>
  )
}

/** Paso 2: responsable sanitario y aviso o licencia. */
function PasoSanitario({ datos, atras, siguiente }: { datos: MiEmpresa; atras: () => void; siguiente: () => void }) {
  const s = t.sanitario
  const { guardarSanitario } = useAccionesRegistro()
  const avisar = useAvisos()
  const e = datos.empresa
  const [v, setV] = useState({
    aviso_funcionamiento: e.aviso_funcionamiento ?? '',
    licencia_sanitaria: e.licencia_sanitaria ?? '',
    responsable_sanitario: e.responsable_sanitario ?? '',
    cedula_responsable: e.cedula_responsable ?? '',
  })
  const [errores, setErrores] = useState<Errores>({})
  const campo = (k: keyof typeof v) => ({ value: v[k], onChange: (ev: { target: { value: string } }) => setV({ ...v, [k]: ev.target.value }) })

  async function enviar(ev: FormEvent) {
    ev.preventDefault()
    const r = validar(esquemaSanitario, v)
    if ('errores' in r) return setErrores(r.errores)
    setErrores({})
    try {
      await guardarSanitario.mutateAsync({ id: e.id, d: r.datos })
      siguiente()
    } catch {
      avisar({ tipo: 'error', titulo: es.errorTitulo, texto: t.errorGenerico })
    }
  }

  return (
    <form className="formulario" onSubmit={(ev) => void enviar(ev)} noValidate>
      <p className="prosa">{s.intro}</p>
      <div className="formulario__rejilla">
        <Campo etiqueta={s.aviso} error={errores.aviso_funcionamiento}>
          {(c) => <input {...c} type="text" {...campo('aviso_funcionamiento')} />}
        </Campo>
        <Campo etiqueta={s.licencia}>{(c) => <input {...c} type="text" {...campo('licencia_sanitaria')} />}</Campo>
        <Campo etiqueta={s.responsable} error={errores.responsable_sanitario}>
          {(c) => <input {...c} type="text" autoComplete="name" required {...campo('responsable_sanitario')} />}
        </Campo>
        <Campo etiqueta={s.cedula} error={errores.cedula_responsable}>
          {(c) => <input {...c} type="text" inputMode="numeric" maxLength={8} required {...campo('cedula_responsable')} />}
        </Campo>
      </div>
      <Botonera atras={atras} enviando={guardarSanitario.isPending} />
    </form>
  )
}

/** Paso 3: sucursal de entrega. */
function PasoSucursal({ datos, atras, siguiente }: { datos: MiEmpresa; atras: () => void; siguiente: () => void }) {
  const s = t.sucursal
  const { guardarSucursal } = useAccionesRegistro()
  const avisar = useAvisos()
  const d = datos.direcciones[0]
  const [v, setV] = useState({
    alias: d?.alias ?? 'Matriz',
    calle: d?.calle ?? '',
    numero_exterior: d?.numero_exterior ?? '',
    numero_interior: d?.numero_interior ?? '',
    colonia: d?.colonia ?? '',
    municipio: d?.municipio ?? '',
    estado_mx: d?.estado_mx ?? '',
    codigo_postal: d?.codigo_postal ?? '',
    contacto_nombre: d?.contacto_nombre ?? '',
    contacto_telefono: d?.contacto_telefono ?? '',
    horario: d?.horario ?? '',
    referencias: d?.referencias ?? '',
  })
  const [errores, setErrores] = useState<Errores>({})
  const campo = (k: keyof typeof v) => ({ value: v[k], onChange: (ev: { target: { value: string } }) => setV({ ...v, [k]: ev.target.value }) })

  async function enviar(ev: FormEvent) {
    ev.preventDefault()
    const r = validar(esquemaSucursal, v)
    if ('errores' in r) return setErrores(r.errores)
    setErrores({})
    try {
      await guardarSucursal.mutateAsync({ id: datos.empresa.id, existente: d?.id, d: r.datos })
      siguiente()
    } catch {
      avisar({ tipo: 'error', titulo: es.errorTitulo, texto: t.errorGenerico })
    }
  }

  const texto = (k: keyof typeof v, etiqueta: string, extra: Record<string, unknown> = {}, requerido = true) => (
    <Campo etiqueta={etiqueta} error={errores[k]}>
      {(c) => <input {...c} type="text" required={requerido} {...extra} {...campo(k)} />}
    </Campo>
  )

  return (
    <form className="formulario" onSubmit={(ev) => void enviar(ev)} noValidate>
      <div className="formulario__rejilla">
        {texto('alias', s.alias)}
        {texto('contacto_nombre', s.contacto, { autoComplete: 'name' })}
        <div className="formulario__ancho">{texto('calle', s.calle, { autoComplete: 'address-line1' })}</div>
        {texto('numero_exterior', s.numeroExterior)}
        {texto('numero_interior', s.numeroInterior, {}, false)}
        {texto('colonia', s.colonia)}
        {texto('municipio', s.municipio)}
        {texto('estado_mx', s.estado, { autoComplete: 'address-level1' })}
        {texto('codigo_postal', s.codigoPostal, { inputMode: 'numeric', maxLength: 5, autoComplete: 'postal-code' })}
        {texto('contacto_telefono', s.telefono, { type: 'tel', inputMode: 'tel', autoComplete: 'tel' })}
        {texto('horario', s.horario, {}, false)}
        <div className="formulario__ancho">
          <Campo etiqueta={s.referencias} error={errores.referencias}>
            {(c) => <textarea {...c} {...campo('referencias')} />}
          </Campo>
        </div>
      </div>
      <Botonera atras={atras} enviando={guardarSucursal.isPending} />
    </form>
  )
}

/** Una fila de documento: estado actual y botón para subir (o reemplazar). */
function FilaDocumento({ tipo, empresaId, existente }: { tipo: TipoDocumento; empresaId: string; existente?: { estado: string } }) {
  const d = t.documentos
  const { subirDocumento } = useAccionesRegistro()
  const avisar = useAvisos()
  const entrada = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  async function alElegir(ev: React.ChangeEvent<HTMLInputElement>) {
    const archivo = ev.target.files?.[0]
    ev.target.value = ''
    if (!archivo) return
    if (!TIPOS_PERMITIDOS.includes(archivo.type)) return setError(d.errorTipo)
    if (archivo.size > PESO_MAXIMO) return setError(d.errorPeso)
    setError(null)
    try {
      await subirDocumento.mutateAsync({ id: empresaId, tipo, archivo })
      avisar({ tipo: 'ok', titulo: d.subido, texto: d.tipos[tipo] })
    } catch {
      setError(d.errorSubida)
    }
  }

  const subiendo = subirDocumento.isPending
  return (
    <li className="documento">
      <div className="documento__info">
        <span className="documento__nombre">{d.tipos[tipo]}</span>
        <StatusMark
          status={existente ? 'done' : 'pending'}
          size={20}
          strokeWidth={2}
          label={existente ? d.subido : undefined}
          color="var(--text-muted)"
          doneColor="var(--ok)"
        />
      </div>
      <input ref={entrada} type="file" accept={TIPOS_PERMITIDOS.join(',')} hidden onChange={(ev) => void alElegir(ev)} aria-label={d.tipos[tipo]} />
      <button type="button" className="boton boton--secundario" disabled={subiendo} aria-busy={subiendo} onClick={() => entrada.current?.click()}>
        <FileUp className="icono" strokeWidth={1.75} aria-hidden="true" />
        {subiendo ? d.subiendo : d.subir}
      </button>
      {error && (
        <p className="campo__error documento__error" role="alert">
          {error}
        </p>
      )}
    </li>
  )
}

/** Paso 4: documentos. Los de aviso o licencia dependen de lo que se capturó en el paso 2. */
function PasoDocumentos({ datos, atras, terminar }: { datos: MiEmpresa; atras: () => void; terminar: () => void }) {
  const d = t.documentos
  const e = datos.empresa
  const tipos: TipoDocumento[] = ['constancia_situacion_fiscal']
  if (e.aviso_funcionamiento) tipos.push('aviso_funcionamiento')
  if (e.licencia_sanitaria) tipos.push('licencia_sanitaria')
  tipos.push('cedula_responsable')
  const completos = documentosCompletos(datos.documentos)

  return (
    <div className="formulario">
      <p className="prosa">{d.intro}</p>
      <ul className="documentos">
        {tipos.map((tipo) => (
          <FilaDocumento key={tipo} tipo={tipo} empresaId={e.id} existente={datos.documentos.find((x) => x.tipo === tipo)} />
        ))}
      </ul>
      {!completos && <p className="campo__ayuda">{d.faltan}</p>}
      <div className="botonera">
        <button type="button" className="boton boton--secundario" onClick={atras}>
          <ArrowLeft className="icono" strokeWidth={1.75} aria-hidden="true" />
          {t.atras}
        </button>
        <button type="button" className="boton boton--primario" disabled={!completos} onClick={terminar}>
          {d.terminar}
        </button>
      </div>
    </div>
  )
}

function Cascaron({ children }: { children: ReactNode }) {
  return (
    <>
      <h1>{t.titulo}</h1>
      {children}
    </>
  )
}

export function RegistroEmpresa() {
  const { cargando, sesion } = useAuth()
  const miEmpresa = useMiEmpresa()
  const [paso, setPaso] = useState<number | null>(null)
  const [terminado, setTerminado] = useState(false)

  useEffect(() => {
    if (paso === null && miEmpresa.isSuccess) setPaso(pasoInicial(miEmpresa.data))
  }, [paso, miEmpresa.isSuccess, miEmpresa.data])

  if (cargando || (sesion && (miEmpresa.isPending || paso === null) && !miEmpresa.isError)) {
    return (
      <Cascaron>
        <div role="status" aria-live="polite">
          <p className="principal__intro">{t.cargando}</p>
          <div className="esqueleto" />
        </div>
      </Cascaron>
    )
  }
  if (!sesion) {
    return (
      <Cascaron>
        <div className="aviso-vacio">
          <p>{t.necesitaSesion}</p>
          <p className="accion">
            <Link className="boton boton--primario" to="/ingresar">
              {es.nav.ingresar}
            </Link>
          </p>
        </div>
      </Cascaron>
    )
  }
  if (miEmpresa.isError) {
    return (
      <Cascaron>
        <div className="aviso-vacio aviso-vacio--error" role="alert">
          <p>{t.errorGenerico}</p>
        </div>
      </Cascaron>
    )
  }

  if (terminado) {
    return (
      <Cascaron>
        <div className="aviso-vacio listo" role="status">
          <StatusMark status="done" size={56} strokeWidth={3} color="var(--ok)" doneColor="var(--ok)" />
          <h2>{t.listoTitulo}</h2>
          <p>{t.listoTexto}</p>
          <p className="accion">
            <Link className="boton boton--primario" to="/mi-empresa">
              {es.miEmpresa.titulo}
            </Link>
          </p>
        </div>
      </Cascaron>
    )
  }

  return (
    <Cascaron>
      <p className="principal__intro prosa">{t.intro}</p>
      <div className="registro">
        <PasosRegistro datos={miEmpresa.data} paso={paso ?? 1} cambiarPaso={setPaso} alTerminar={() => setTerminado(true)} />
      </div>
    </Cascaron>
  )
}

/** Los cuatro pasos dentro del Stepper. Aparte para poder revisarlos en la guía visual (solo desarrollo). */
export function PasosRegistro({
  datos,
  paso,
  cambiarPaso,
  alTerminar,
}: {
  datos: MiEmpresa | null | undefined
  paso: number
  cambiarPaso: (paso: number) => void
  alTerminar: () => void
}) {
  return (
    <Stepper step={paso} onStepChange={cambiarPaso} showFooter={false} frame={false} theme="light" accentColor="#1a5fc8" width="100%" linear>
      <Step title={t.pasos[0]}>
        <PasoFiscal datos={datos} siguiente={() => cambiarPaso(2)} />
      </Step>
      <Step title={t.pasos[1]}>{datos && <PasoSanitario datos={datos} atras={() => cambiarPaso(1)} siguiente={() => cambiarPaso(3)} />}</Step>
      <Step title={t.pasos[2]}>{datos && <PasoSucursal datos={datos} atras={() => cambiarPaso(2)} siguiente={() => cambiarPaso(4)} />}</Step>
      <Step title={t.pasos[3]}>{datos && <PasoDocumentos datos={datos} atras={() => cambiarPaso(3)} terminar={alTerminar} />}</Step>
    </Stepper>
  )
}
